import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { useApi, type WhanauListResponse, ApiError } from "../api/client";
import { useTelemetry } from "../telemetry/AppInsightsProvider";
import { useTileSettings } from "../api/useTileSettings";
import { useCurrentUser } from "../api/useCurrentUser";
import "./HomeView.css";

/**
 * Kaimahi home screen at "/".
 *
 * Replaces the previous landing-via-DirectoryView pattern. A kaimahi
 * signing in arrives here rather than dropping straight into the whānau
 * list — gives them an at-a-glance read on their caseload and a clear
 * set of next-step actions.
 *
 * No new API endpoints — stats and the recent-caseload preview are both
 * derived client-side from the existing /api/v1/whanau response.
 *
 * Tablet posture: large hit targets (--hit-target / 44px enforced via
 * .home-action and .home-caseload-card), card-style action affordances,
 * generous spacing.
 */

type WhanauListState =
  | { kind: "loading" }
  | { kind: "ok"; data: WhanauListResponse }
  | { kind: "error"; message: string };

export function HomeView() {
  const { t } = useTranslation();
  const callApi = useApi();
  const telemetry = useTelemetry();
  const tileSettings = useTileSettings();
  const me = useCurrentUser();

  // Stable tile IDs match the catalogue in src/functions/managerViews.ts
  // and the migration's documentation block. If the /me call is in flight
  // we render everything (managers see their full home, then tiles fold
  // away if a hidden entry exists — better than a flash of "nothing here").
  const hiddenTiles = useMemo<ReadonlySet<string>>(() => {
    if (me.kind !== "ok") return new Set<string>();
    return new Set(me.data.view_preferences?.home_tiles_hidden ?? []);
  }, [me]);
  const showStats = !hiddenTiles.has("home.stats");
  const showDirectory = !hiddenTiles.has("home.action.directory");
  const showFramework = !hiddenTiles.has("home.action.framework");
  const showCaseload = !hiddenTiles.has("home.caseload.recent");
  const showActionsSection = showDirectory || showFramework;

  const [list, setList] = useState<WhanauListState>({ kind: "loading" });

  useEffect(() => {
    telemetry.trackEvent("home.viewed");
  }, [telemetry]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const data = await callApi<WhanauListResponse>("/whanau");
        if (cancelled) return;
        setList({ kind: "ok", data });
      } catch (err) {
        if (cancelled) return;
        const message =
          err instanceof ApiError
            ? `${String(err.status)} ${err.message}`
            : err instanceof Error
              ? err.message
              : "Unknown error";
        setList({ kind: "error", message });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [callApi]);

  const stats = useMemo(() => {
    if (list.kind !== "ok") return null;
    const rows = list.data.rows;
    const total = rows.length;
    const active = rows.filter((r) => r.engagement_state === "active").length;
    // "New this month" — registered within the last 30 days. A soft
    // proxy for "started recently"; we don't have a "needs attention"
    // signal in the list payload yet so this stands in.
    const thirtyDaysAgo = Date.now() - 30 * 24 * 60 * 60 * 1000;
    const newThisMonth = rows.filter(
      (r) => new Date(r.registered_at).getTime() >= thirtyDaysAgo,
    ).length;
    return { total, active, newThisMonth };
  }, [list]);

  const recentCaseload = useMemo(() => {
    if (list.kind !== "ok") return [];
    // The list comes pre-sorted by registered_at desc. Show the first
    // five.
    return list.data.rows.slice(0, 16);
  }, [list]);

  const tiles = useMemo(() => {
    const get = (id: "directory" | "framework") => {
      const override =
        tileSettings.kind === "ok" ? tileSettings.map.get(id) : undefined;
      return {
        title:
          (override?.title && override.title.length > 0
            ? override.title
            : null) ?? t(`home.action.${id}.title`),
        desc:
          (override?.description && override.description.length > 0
            ? override.description
            : null) ?? t(`home.action.${id}.desc`),
        photo: override?.photo_url ?? `/${id}-tile.jpg`,
      };
    };
    return {
      directory: get("directory"),
      framework: get("framework"),
    };
  }, [tileSettings, t]);

  const handleActionClick = (action: string) => {
    telemetry.trackEvent("home.action_taken", { action });
  };

  return (
    <div className="home-view">
      {showStats && (
        <section className="home-stats" aria-label={t("home.statsLabel")}>
          <StatTile
            label={t("home.stat.caseload")}
            value={stats ? stats.total : "—"}
            loading={list.kind === "loading"}
          />
          <StatTile
            label={t("home.stat.active")}
            value={stats ? stats.active : "—"}
            loading={list.kind === "loading"}
            tone="teal"
          />
          <StatTile
            label={t("home.stat.newThisMonth")}
            value={stats ? stats.newThisMonth : "—"}
            loading={list.kind === "loading"}
            tone="amber"
          />
        </section>
      )}

      {showActionsSection && (
        <section className="home-actions" aria-label={t("home.actionsLabel")}>
          {showDirectory && (
            <Link
              to="/directory"
              className="home-action"
              onClick={() => handleActionClick("directory")}
            >
              <img
                src={tiles.directory.photo}
                alt=""
                className="home-action__image"
              />
              <span className="home-action__title">
                {tiles.directory.title}
              </span>
              <span className="home-action__desc">{tiles.directory.desc}</span>
              <span className="home-action__arrow" aria-hidden="true">
                →
              </span>
            </Link>
          )}
          {showFramework && (
            <Link
              to="/framework"
              className="home-action"
              onClick={() => handleActionClick("framework")}
            >
              <img
                src={tiles.framework.photo}
                alt=""
                className="home-action__image"
              />
              <span className="home-action__title">
                {tiles.framework.title}
              </span>
              <span className="home-action__desc">{tiles.framework.desc}</span>
              <span className="home-action__arrow" aria-hidden="true">
                →
              </span>
            </Link>
          )}
        </section>
      )}

      {showCaseload && (
        <section
          className="home-caseload"
          aria-label={t("home.caseload.label")}
        >
          <header className="home-caseload__header">
            <h2 className="home-caseload__heading">
              {t("home.caseload.heading")}
            </h2>
            {list.kind === "ok" && recentCaseload.length > 0 && (
              <Link
                to="/directory/open"
                className="home-caseload__see-all"
                onClick={() => handleActionClick("caseload.see_all")}
              >
                {t("home.caseload.seeAll", { count: list.data.count })} →
              </Link>
            )}
          </header>

          {list.kind === "loading" && (
            <p className="home-caseload__loading">
              {t("home.caseload.loading")}
            </p>
          )}

          {list.kind === "error" && (
            <p className="home-caseload__error">
              {t("home.caseload.error", { message: list.message })}
            </p>
          )}

          {list.kind === "ok" && recentCaseload.length === 0 && (
            <p className="home-caseload__empty">{t("home.caseload.empty")}</p>
          )}

          {list.kind === "ok" && recentCaseload.length > 0 && (
            <ul className="home-caseload__list">
              {recentCaseload.map((w) => (
                <li key={w.whanau_id}>
                  <Link
                    to={`/whanau/${String(w.whanau_id)}`}
                    className="home-caseload-card"
                    onClick={() => handleActionClick("caseload.whanau_open")}
                  >
                    <span className="home-caseload-card__name">
                      {w.display_name}
                    </span>
                    <span className="home-caseload-card__meta">
                      {w.primary_location ?? w.service_id}
                      <span aria-hidden="true"> · </span>
                      <span
                        className={`home-caseload-card__state home-caseload-card__state--${w.engagement_state}`}
                      >
                        {t(
                          `home.engagement.${w.engagement_state}`,
                          w.engagement_state,
                        )}
                      </span>
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      <p className="home-view__pou">
        <span lang="mi">Te Pou Ihorangi</span>
        <span aria-hidden="true"> · </span>
        <span lang="mi">Te Pou o Hineahuone</span>
        <span aria-hidden="true"> · </span>
        <span lang="mi">Te Pou Aotūroa</span>
        <span aria-hidden="true"> · </span>
        <span lang="mi">Te Pou Hinetītama</span>
      </p>
    </div>
  );
}

type StatTileProps = {
  label: string;
  value: number | string;
  loading?: boolean;
  tone?: "navy" | "teal" | "amber";
};

function StatTile({ label, value, loading, tone = "navy" }: StatTileProps) {
  return (
    <div className={`home-stat home-stat--${tone}`} aria-busy={loading}>
      <span className="home-stat__value">{value}</span>
      <span className="home-stat__label">{label}</span>
    </div>
  );
}

export default HomeView;
